import React, { useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/react-hooks'
import memoize from 'memoize-one'
import queryString from 'query-string'
import { Route, Switch, useLocation, useParams } from 'react-router-dom'
import TitleBar from './TitleBar'
import FileDetails from './FileDetails'
import ItemPermissions from '../ItemPermissions'
import ItemChildren from './ItemChildren'
import routes from '../../routes'
import ItemView from '../itemView/ItemView'
import { get } from 'lodash'
import '../../styles/item/Item.css'
import ItemFiles from './ItemFiles'
import { addSelectedChanges } from '../FileManagement/FileHelperFunctions'
import { FRAGMENTS_FILE } from '../../utils/graphqlFragments'

// const SLOW_QUERY_TOAST_MS = 5000
const DEFAULT_MAX = 10

const GET_ITEM = gql`
  ${FRAGMENTS_FILE}
  query GetItem(
    $id: ID!
  ) {
    item(id: $id) {
      id
      title
      hasChildren
      parentId
      subTitle
      fileConnection {
        files {
          ...fileFields
        }
        totalCount
      }
      permissions {
        currentUser {
          canWrite
        }
      }
      distributionLinks{ 
        uri
      }
    }
    fileDeleted(itemSb2Id: $id) {
      id
      version
      class
      itemId
      name
      contentType
      size
      lastModified
      bucket
      key
      dateCreated
      createdBy
      lastUpdated
      lastUpdatedBy
      cuid
      published
      imageHeight
      imageWidth
      processStatus
    }
  }
`

export default function Item() {
  const { id } = useParams()
  const location = useLocation()
  const search = queryString.parse(location.search)
  const [selectedRows, setSelectedRows] = useState([])

  const genQueryVariables = memoize((id) => {
    return {
      id,
      max: parseInt(search.max) || DEFAULT_MAX,
      offset: (search.page - 1 || 0) * (search.max || DEFAULT_MAX),
      sort: search.sort,
      order: search.order,
    }
  })

  const { loading, error, data } = useQuery(GET_ITEM, {
    // notifyOnNetworkStatusChange: true,
    variables: genQueryVariables(id),
    fetchPolicy: 'cache-and-network',
  })

  const item = get(data, 'item', {})
  const files = get(item, 'fileConnection.files', {})
  const distributionLinks = get(item, 'distributionLinks', null)
  const deletedFiles = get(data, 'fileDeleted', [])
  const file = []
  for (let i = 0; i < files.length; i++) {
    file[files[i].key] = files[i]
  }

  const addChanges = (row, textChange, previewChange) => {
    addSelectedChanges(row, textChange, previewChange, selectedRows, file)
  }
  const resetSelectedRows = () => {
    setSelectedRows([])
  }

  return (
    <div
      className={
        'container ' + (loading ? 'sb-item-border-loading' : 'sb-item-border')
      }
    >
      <div className={error ? 'alert alert-danger' : 'd-none'} role="alert">
        An error occurred while loading the item with ID={id}.
      </div>
      <TitleBar
        item={item}
        selectedRows={selectedRows}
        resetSelectedRows={resetSelectedRows}
      />
      <Switch>
        <Route path={`${routes.itemPath}/:id/files`}>
          <ItemFiles
            addChanges={addChanges}
            selectedRows={selectedRows}
            item={item}
            deletedFiles={deletedFiles}
            distributionLinks={distributionLinks}
            queryVariables={genQueryVariables(id)}
          />
        </Route>
        <Route
          path={`${routes.itemPath}/:id/file/:fileIdentifier`}
          component={(props) => <FileDetails item={item} />}
        />
        <Route path={`${routes.itemPath}/:id/children`}>
          <ItemChildren item={item} queryVariables={genQueryVariables(id)} />
        </Route>
        <Route path={`${routes.itemPath}/:id/permissions`}>
          <ItemPermissions id={id} />
        </Route>

        <Route path={`${routes.itemPath}/:id/new`}></Route>
        <Route
          path={[
            `${routes.itemPath}/:id/what`,
            `${routes.itemPath}/:id/who`,
            `${routes.itemPath}/:id/when`,
            `${routes.itemPath}/:id/where`,
            `${routes.itemPath}/:id/whatelse`,
            `${routes.itemPath}/:id/tags`,
          ]}
        ></Route>
        <Route path={`${routes.itemPath}/:id/view`}>
          <ItemView item={item} />
        </Route>

        <Route>
          <ItemFiles
            addChanges={addChanges}
            selectedRows={selectedRows}
            item={item}
            deletedFiles={deletedFiles}
            distributionLinks={distributionLinks}
            queryVariables={genQueryVariables(id)}
          />
        </Route>
      </Switch>
    </div>
  )
}
